import React, { FC, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import padStart from 'lodash/padStart'
import DownloadIcon from 'transfix-icons/build/DownloadIcon'
import { Button } from 'transfix-ui/components/Button'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import { callApi, ENDPOINTS } from 'utils/api/callApi'

const LineNumber = (props: { num: number }) => {
  const { num } = props
  // TODO: add a separator between line nums & text
  return <span className='tr pa3'>{padStart((num + 1).toString(), 3)}.</span>
}

const Segment = (props: { text: string; separator: string }) => {
  const { separator, text } = props
  const segmentValues = text.split(separator)
  const segmentName = segmentValues.shift()
  return (
    <span>
      <span className='body-bold'>{segmentName}</span>
      {segmentValues.map((segmentValue, i) => {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i}>
            <span className='pa1'>{separator}</span>
            <span>{segmentValue}</span>
          </span>
        )
      })}
    </span>
  )
}

const EdiContent = (props: { text: string }) => {
  const { text } = props

  // TODO: determine what the segment separator is - assuming ~
  // TODO: better error handling for value separator match instead of assuming *
  const valueSeparatorMatch = text.match('ISA(.)')
  const valueSeparator = valueSeparatorMatch ? valueSeparatorMatch[1] : '*'

  const countCarriage = (text.match(/\r/) || []).length
  const countTilde = (text.match(/~/g) || []).length
  const countBar = (text.match(/\|/g) || []).length
  // Default to tilde character for splitting segments
  let segmentSeparator = '~'
  // When bars are present but there's more tilde's, take that as a sign
  // that tidle is being used as an element separator and try to use bar
  // as the segment separator
  if (countTilde > countBar && countBar > 0) {
    segmentSeparator = '|'
  } else if (countCarriage > 0) {
    // Fall back to a carriage return
    segmentSeparator = '\r'
  }

  let ediLines = text.trim().split('\n')
  if (ediLines.length === 1) {
    ediLines = text
      .split(segmentSeparator)
      .slice(0, -1)
      .map(line => `${line}${segmentSeparator}`)
  }

  return (
    <div>
      <pre>
        {ediLines.map((ediLine, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i}>
            <LineNumber num={i} />
            <Segment text={ediLine} separator={valueSeparator} />
          </div>
        ))}
      </pre>
    </div>
  )
}

export const RawEdiFileDetails: FC = () => {
  const { id: ediFileId } = useParams<{ id: string }>()
  const { direction: ediFileDirection } = useParams<{ direction: string }>()

  const [ediContent, setEdiContent] = useState<string>('')
  const [s3Key, setS3Key] = useState<string>('sample.edi')
  const [showDownloadIcon, setShowDownloadIcon] = useState<boolean>(false)
  const [loading, setLoading] = useState(false)
  const [loadingError, setLoadingError] = useState(false)

  const fetchDataFromApi = async () => {
    setLoading(true)
    setLoadingError(false)

    try {
      const res: any = await callApi({
        endpoint: ENDPOINTS.ediFilesMapper,
        id: ediFileId,
        params: { objectType: ediFileDirection },
        method: 'GET',
      })
      setLoading(false)
      setShowDownloadIcon(true)
      setEdiContent(res.rawEdiFile.ediFileData)
      setS3Key(res.rawEdiFile.s3Key)
    } catch (e) {
      setLoading(false)
      setLoadingError(true)
    }
  }

  const downloadClickHandler = () => {
    const element = document.createElement('a')
    const file = new Blob([ediContent], { type: 'text/plain' })
    element.href = URL.createObjectURL(file)
    element.download = s3Key
    document.body.appendChild(element) // Required for this to work in FireFox
    element.click()
  }

  useEffect(() => {
    fetchDataFromApi()
  }, [])

  return (
    <div className='flex justify-center'>
      {!loading && !ediContent && (
        <InlineMessage
          message='There is no EDI file content available at that ID'
          type={INLINE_MESSAGE_VARIANTS.INFO}
        />
      )}
      {!loading && loadingError && (
        <InlineMessage
          message='There was an error loading the EDI file content.'
          type={INLINE_MESSAGE_VARIANTS.ALERT}
        />
      )}
      {loading && <Spinner position='relative' />}
      <div>
        {showDownloadIcon && (
          <div style={{ width: '50%', margin: '0 auto' }}>
            <Button
              startIcon={<DownloadIcon />}
              onClick={downloadClickHandler}
              data-test='download-raw-edi'>
              Contents
            </Button>
          </div>
        )}
        {!loading && ediContent ? <EdiContent text={ediContent} /> : ''}
      </div>
    </div>
  )
}
